import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Truck, Inbox } from 'lucide-react';
import { SchedulingIndicator, formatShortLocation, ApptTime, dropDays } from './kanbanUtils';
import { CbCardInfo } from './CrossBorderCard';

export const useIsMobile = (maxWidth = 767) => {
  const [mobile, setMobile] = useState(() => window.matchMedia(`(max-width: ${maxWidth}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [maxWidth]);
  return mobile;
};

const StopLine = ({ label, stop, facilities, testId }) => (
  <div className="mkb-stop">
    <span className="mkb-stop-label">{label}</span>
    <SchedulingIndicator status={stop?.appointment_status} />
    <span className="mkb-stop-city">{stop ? (stop.facility_name || formatShortLocation(stop, facilities)) : 'N/A'}</span>
    <ApptTime value={stop?.appointment_datetime} endTime={stop?.appointment_end_time} testId={testId} />
  </div>
);

const DaysBadge = ({ days, testId }) => (
  days === null || days === undefined ? null : (
    <span className="mkb-days" data-testid={testId}>{days}d</span>
  )
);

const MobileLoadCard = ({ load, facilities, mode, onToggleTracking }) => {
  const navigate = useNavigate();
  const pickups = (load.stops || []).filter(s => s.type === 'pickup');
  const deliveries = (load.stops || []).filter(s => s.type === 'delivery');
  const dropStops = (load.stops || []).filter(s => s.drop_off && s.trailer_dropped_date);
  const cbDays = mode === 'xb' ? Math.max(0, Math.round((Date.now() - (load.cb_status_changed_at ? new Date(load.cb_status_changed_at).getTime() : Date.now())) / 86400000)) : null;

  return (
    <article
      className="mkb-card"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/loads/${load.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/loads/${load.id}`); }}
      data-testid={`mobile-load-card-${load.id}`}
    >
      <div className="mkb-card-head">
        <span className="mkb-load-number">{load.load_number || load.id.substring(0, 8).toUpperCase()}</span>
        <span className="mkb-head-right">
          {load.cross_border && <span className="mkb-xb">XB</span>}
          {mode === 'drop' && <DaysBadge days={dropStops.length ? dropDays(dropStops[dropStops.length - 1]) : null} testId={`mobile-drop-days-${load.id}`} />}
          {mode === 'xb' && <DaysBadge days={cbDays} testId={`mobile-cb-days-${load.id}`} />}
          <ChevronRight className="w-5 h-5 text-[var(--tms-text-muted)]" />
        </span>
      </div>
      <p className="mkb-customer">{load.customer_name || 'No customer'}</p>
      <p className="mkb-carrier">
        <Truck className="w-3.5 h-3.5" />
        <span className={load.carrier_name ? '' : 'italic'}>{load.carrier_name || 'No carrier'}</span>
      </p>
      <div className="mkb-stops">
        <StopLine label="PU" stop={pickups[0]} facilities={facilities} testId={`mobile-card-pu-appt-${load.id}`} />
        {deliveries.length ? deliveries.map((d, i) => (
          <StopLine key={i} label={deliveries.length > 1 ? `D${i + 1}` : 'DEL'} stop={d} facilities={facilities} testId={`mobile-card-del-appt-${load.id}-${i}`} />
        )) : <StopLine label="DEL" stop={null} facilities={facilities} />}
      </div>
      {mode === 'xb' && <CbCardInfo cb={load.cb} loadId={`m-${load.id}`} />}
      {onToggleTracking && (
        <label className="mkb-tracking" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!load.tracking}
            onChange={(e) => onToggleTracking(load.id, e.target.checked)}
            data-testid={`mobile-tracking-checkbox-${load.id}`}
          />
          Tracking
        </label>
      )}
    </article>
  );
};

const MobileStopCard = ({ item, facilities }) => {
  const navigate = useNavigate();
  const { load, stop, stopIndex } = item;
  const isPickup = stop.type === 'pickup';
  const ref = isPickup ? (stop.references?.[0] || '') : (stop.po_number || '');
  return (
    <article className="mkb-card" role="button" tabIndex={0} onClick={() => navigate(`/loads/${load.id}`)} data-testid={`mobile-stop-card-${load.id}-${stopIndex}`}>
      <div className="mkb-card-head">
        <span className="mkb-load-number">{load.load_number || load.id.substring(0, 8).toUpperCase()}</span>
        <span className="mkb-head-right">
          <span className={`mkb-stop-type ${isPickup ? 'is-pickup' : 'is-delivery'}`}>{isPickup ? 'PICKUP' : 'DELIVERY'} {stop.stop_number || stopIndex + 1}</span>
          <ChevronRight className="w-5 h-5 text-[var(--tms-text-muted)]" />
        </span>
      </div>
      <p className="mkb-customer">{load.customer_name}</p>
      <div className="mkb-stops">
        <StopLine label={isPickup ? 'PU' : 'DEL'} stop={stop} facilities={facilities} testId={`mobile-stop-card-appt-${load.id}-${stopIndex}`} />
      </div>
      {ref && <p className="mkb-carrier font-mono">{isPickup ? 'PU Ref' : 'PO'} {ref}</p>}
    </article>
  );
};

// Trello-style mobile board: one column per screen, swipe between columns, scroll cards vertically
export const MobileKanbanBoard = ({ columns, grouped, facilities, mode, onToggleTracking, unitLabel = 'loads' }) => {
  const boardRef = useRef(null);
  const tabsRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => { setActive(a => Math.min(a, Math.max(columns.length - 1, 0))); }, [columns.length]);

  const handleScroll = () => {
    const board = boardRef.current;
    if (!board) return;
    const center = board.scrollLeft + board.clientWidth / 2;
    let best = 0, bestDist = Infinity;
    [...board.children].forEach((el, i) => {
      const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    if (best !== active) setActive(best);
  };

  useEffect(() => {
    tabsRef.current?.children[active]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [active]);

  const goTo = (i) => {
    const el = boardRef.current?.children[i];
    if (el) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  };

  if (!columns.length) {
    return <div className="mkb-empty-board" data-testid="mobile-kanban-empty">No columns selected. Use the Columns button to add some.</div>;
  }

  return (
    <div className="mkb" data-testid="mobile-kanban">
      <div className="mkb-tabs" ref={tabsRef} data-testid="mobile-kanban-tabs">
        {columns.map((c, i) => (
          <button key={c.id} className={`mkb-tab ${i === active ? 'is-active' : ''}`} onClick={() => goTo(i)} data-testid={`mobile-kanban-tab-${c.id}`}>
            {c.title}<span className="mkb-tab-count">{(grouped[c.id] || []).length}</span>
          </button>
        ))}
      </div>
      <div className="mkb-board" ref={boardRef} onScroll={handleScroll} data-testid="mobile-kanban-board">
        {columns.map((c) => {
          const items = grouped[c.id] || [];
          return (
            <section key={c.id} className="mkb-col" data-testid={`mobile-column-${c.id}`}>
              <header className="mkb-col-head">
                <h3>{c.title}</h3>
                <span>{items.length} {unitLabel}</span>
              </header>
              <div className="mkb-cards">
                {items.length === 0 ? (
                  <div className="mkb-col-empty"><Inbox className="w-6 h-6" /><span>No {unitLabel} here</span></div>
                ) : items.map((item) => (
                  mode === 'sched'
                    ? <MobileStopCard key={item.id} item={item} facilities={facilities} />
                    : <MobileLoadCard key={item.id} load={item} facilities={facilities} mode={mode} onToggleTracking={mode === 'tracking' ? onToggleTracking : undefined} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <div className="mkb-dots" aria-hidden="true">
        {columns.map((c, i) => <span key={c.id} className={`mkb-dot ${i === active ? 'is-active' : ''}`} />)}
      </div>
    </div>
  );
};
